import {
  useEffect,
  useId,
  useRef,
  useState,
  useCallback,
  type DragEvent,
} from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import { Icon } from "../ui/Icon";
import { cn } from "../../lib/utils";
import { formatFileSize } from "../../lib/utils";
import { useSubmitFeedback } from "../../hooks/useFeedback";
import { FeedbackType, FeedbackPriority } from "../../types/feedback";
import CloseCircleLinear from "~icons/solar/close-circle-linear";
import BugLinear from "~icons/solar/bug-linear";
import ChatRoundDotsLinear from "~icons/solar/chat-round-dots-linear";
import LightbulbLinear from "~icons/solar/lightbulb-linear";
import FileSendLinear from "~icons/solar/file-send-linear";
import CloseSquareLinear from "~icons/solar/close-square-linear";
import DocumentLinear from "~icons/solar/document-linear";
import GalleryLinear from "~icons/solar/gallery-linear";
import LinkRoundLinear from "~icons/solar/link-round-linear";

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const feedbackSchema = z.object({
  type: z.enum([
    FeedbackType.BUG,
    FeedbackType.FEEDBACK,
    FeedbackType.FEATURE_REQUEST,
  ]),
  title: z
    .string()
    .min(1, "Title is required")
    .max(200, "Title must be 200 characters or less"),
  description: z
    .string()
    .min(1, "Description is required")
    .max(5000, "Description must be 5000 characters or less"),
  url: z
    .string()
    .url("Please enter a valid URL")
    .max(2000, "URL must be 2000 characters or less")
    .optional()
    .or(z.literal("")),
  priority: z.enum([
    FeedbackPriority.LOW,
    FeedbackPriority.MEDIUM,
    FeedbackPriority.HIGH,
    FeedbackPriority.CRITICAL,
  ]),
});

type FeedbackFormData = z.infer<typeof feedbackSchema>;

// ---------------------------------------------------------------------------
// Type selector config
// ---------------------------------------------------------------------------

const TYPE_OPTIONS = [
  {
    value: FeedbackType.BUG,
    label: "Bug",
    icon: BugLinear,
    activeClass: "border-red-300 bg-red-50 text-status-red-text",
    inactiveClass:
      "border-[#e8e8e9] text-text-secondary hover:border-red-200 hover:bg-red-50/50",
  },
  {
    value: FeedbackType.FEEDBACK,
    label: "Feedback",
    icon: ChatRoundDotsLinear,
    activeClass: "border-blue-300 bg-blue-50 text-status-blue-text",
    inactiveClass:
      "border-[#e8e8e9] text-text-secondary hover:border-blue-200 hover:bg-blue-50/50",
  },
  {
    value: FeedbackType.FEATURE_REQUEST,
    label: "Feature Request",
    icon: LightbulbLinear,
    activeClass: "border-green-300 bg-green-50 text-status-green-text",
    inactiveClass:
      "border-[#e8e8e9] text-text-secondary hover:border-green-200 hover:bg-green-50/50",
  },
] as const;

// ---------------------------------------------------------------------------
// Shared style constants
// ---------------------------------------------------------------------------

const LABEL_CLASSES = "text-sm font-medium text-[#232627] mb-1.5 block";
const INPUT_CLASSES =
  "w-full border border-[#e8e8e9] rounded-lg px-4 py-3 text-sm outline-none focus:border-primary transition-colors placeholder:text-text-placeholder";

// ---------------------------------------------------------------------------
// Staged file for attachment preview
// ---------------------------------------------------------------------------

interface StagedFile {
  id: string;
  file: File;
  preview?: string; // object URL for images
}

// ---------------------------------------------------------------------------
// Attachment drop zone sub-component
// ---------------------------------------------------------------------------

interface AttachmentZoneProps {
  files: StagedFile[];
  onAdd: (incoming: FileList | File[]) => void;
  onRemove: (id: string) => void;
}

function AttachmentZone({ files, onAdd, onRemove }: AttachmentZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      onAdd(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onAdd(e.target.files);
      e.target.value = "";
    }
  };

  const hasFiles = files.length > 0;

  return (
    <div>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "rounded-xl border border-[#e8e8e9] bg-white transition-colors",
          isDragging && "border-primary bg-primary/5"
        )}
      >
        {hasFiles ? (
          <div className="flex flex-col gap-2 p-3">
            {files.map((staged) => (
              <div
                key={staged.id}
                className="flex items-center gap-3 rounded-lg border border-[#e8e8e9] bg-white p-3"
              >
                {/* Thumbnail or file icon */}
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-grey-25">
                  {staged.preview ? (
                    <img
                      src={staged.preview}
                      alt={staged.file.name}
                      className="size-10 rounded-lg object-cover"
                    />
                  ) : (
                    <Icon
                      icon={DocumentLinear}
                      size="md"
                      className="text-grey"
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-body-sm font-medium text-text-primary">
                    {staged.file.name}
                  </p>
                  <p className="text-body-xs text-text-secondary">
                    {formatFileSize(staged.file.size)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(staged.id)}
                  aria-label={`Remove ${staged.file.name}`}
                  className="shrink-0 text-grey transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 rounded"
                >
                  <Icon icon={CloseSquareLinear} size="sm" />
                </button>
              </div>
            ))}
            {files.length < MAX_FILES && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-full border border-[#b7b8bc] px-4 py-1.5 text-body-sm font-semibold text-text-primary transition-colors hover:bg-grey-25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
              >
                <Icon icon={FileSendLinear} size="xs" />
                Add More
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 px-6 py-8 text-center">
            <div className="flex size-10 items-center justify-center rounded-full bg-primary/10">
              <Icon icon={GalleryLinear} size="md" className="text-primary" />
            </div>
            <p className="text-body-sm text-[#69686d]">
              Drag and drop images or files here
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#b7b8bc] px-4 py-1.5 text-body-sm font-semibold text-text-primary transition-colors hover:bg-grey-25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
            >
              Add File
            </button>
            <p className="text-body-xs text-text-secondary">
              Max {MAX_FILES} files, {formatFileSize(MAX_FILE_SIZE)} each
            </p>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,image/gif,image/webp,text/plain"
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={handleFileInputChange}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface FeedbackModalProps {
  open: boolean;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// FeedbackModal
// ---------------------------------------------------------------------------

export function FeedbackModal({ open, onClose }: FeedbackModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  const submitFeedback = useSubmitFeedback(onClose);

  // Sync dialog open/close
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open) {
      if (!dialog.open) dialog.showModal();
    } else {
      if (dialog.open) dialog.close();
    }

    const handleClose = () => onCloseRef.current();
    dialog.addEventListener("close", handleClose);
    return () => dialog.removeEventListener("close", handleClose);
  }, [open]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    mode: "onBlur",
    defaultValues: {
      type: FeedbackType.FEEDBACK,
      title: "",
      description: "",
      url: "",
      priority: FeedbackPriority.MEDIUM,
    },
  });

  // Reset form and files when modal opens
  useEffect(() => {
    if (open) {
      reset({
        type: FeedbackType.FEEDBACK,
        title: "",
        description: "",
        url: "",
        priority: FeedbackPriority.MEDIUM,
      });
      // Revoke old object URLs
      for (const f of stagedFiles) {
        if (f.preview) URL.revokeObjectURL(f.preview);
      }
      setStagedFiles([]);
      setFileError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      for (const f of stagedFiles) {
        if (f.preview) URL.revokeObjectURL(f.preview);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addFiles = useCallback((incoming: FileList | File[]) => {
    const files = Array.from(incoming);
    setFileError(null);

    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE);
    if (oversized.length > 0) {
      setFileError(
        `Some files exceed the ${formatFileSize(MAX_FILE_SIZE)} limit and were not added.`
      );
    }

    const valid = files.filter((f) => f.size <= MAX_FILE_SIZE);
    setStagedFiles((prev) => {
      const remaining = MAX_FILES - prev.length;
      const toAdd = valid.slice(0, remaining);
      if (valid.length > remaining) {
        setFileError(`Maximum ${MAX_FILES} files allowed.`);
      }
      return [
        ...prev,
        ...toAdd.map((file) => ({
          id: crypto.randomUUID(),
          file,
          preview: file.type.startsWith("image/")
            ? URL.createObjectURL(file)
            : undefined,
        })),
      ];
    });
  }, []);

  const removeFile = useCallback((id: string) => {
    setStagedFiles((prev) => {
      const removed = prev.find((f) => f.id === id);
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleFormSubmit = async (data: FeedbackFormData) => {
    const { url, ...rest } = data;
    await submitFeedback.mutateAsync({
      ...rest,
      ...(url ? { url } : {}),
      page_url: window.location.pathname,
      browser_info: navigator.userAgent,
      files: stagedFiles.map((f) => f.file),
    });
  };

  // Stable IDs for accessibility
  const titleId = useId();
  const descriptionId = useId();
  const urlId = useId();
  const priorityId = useId();

  const isBusy = isSubmitting || submitFeedback.isPending;

  return (
    <dialog
      ref={dialogRef}
      aria-label="Submit Feedback"
      onClick={handleBackdropClick}
      className="m-auto h-fit w-full max-w-2xl rounded-2xl bg-white p-0 motion-safe:animate-modal-in backdrop:bg-black/50 backdrop:motion-safe:animate-modal-backdrop-in"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#f0f0f0] px-6 py-5">
        <h2 className="text-body-xl font-semibold text-text-primary">
          Share Feedback
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close modal"
          className="cursor-pointer text-grey transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <Icon icon={CloseCircleLinear} size="xl" />
        </button>
      </div>

      {/* Form body */}
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="flex max-h-[70vh] flex-col gap-5 overflow-y-auto px-6 py-5">
          {/* Type selector */}
          <div className="flex flex-col gap-1.5">
            <span className={LABEL_CLASSES}>Type</span>
            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <div
                  className="flex gap-2"
                  role="group"
                  aria-label="Feedback type"
                >
                  {TYPE_OPTIONS.map((opt) => {
                    const isActive = field.value === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        onClick={() => field.onChange(opt.value)}
                        className={cn(
                          "flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2.5 text-body-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
                          isActive ? opt.activeClass : opt.inactiveClass
                        )}
                      >
                        <Icon icon={opt.icon} size="sm" />
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}
            />
          </div>

          {/* Title */}
          <div className="flex flex-col">
            <label htmlFor={titleId} className={LABEL_CLASSES}>
              Title *
            </label>
            <input
              id={titleId}
              type="text"
              placeholder="Brief summary of your feedback..."
              className={cn(
                INPUT_CLASSES,
                errors.title && "border-error focus:border-error"
              )}
              {...register("title")}
            />
            {errors.title && (
              <p role="alert" className="mt-1 text-xs text-error">
                {errors.title.message}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="flex flex-col">
            <label htmlFor={descriptionId} className={LABEL_CLASSES}>
              Description *
            </label>
            <textarea
              id={descriptionId}
              placeholder="Describe in detail..."
              rows={5}
              className={cn(
                INPUT_CLASSES,
                "resize-none",
                errors.description && "border-error focus:border-error"
              )}
              {...register("description")}
            />
            {errors.description && (
              <p role="alert" className="mt-1 text-xs text-error">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* URL */}
          <div className="flex flex-col">
            <label htmlFor={urlId} className={LABEL_CLASSES}>
              URL
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-grey">
                <Icon icon={LinkRoundLinear} size="sm" />
              </span>
              <input
                id={urlId}
                type="url"
                placeholder="https://example.com"
                className={cn(
                  INPUT_CLASSES,
                  "pl-9",
                  errors.url && "border-error focus:border-error"
                )}
                {...register("url")}
              />
            </div>
            {errors.url && (
              <p role="alert" className="mt-1 text-xs text-error">
                {errors.url.message}
              </p>
            )}
          </div>

          {/* Priority */}
          <div className="flex flex-col">
            <label htmlFor={priorityId} className={LABEL_CLASSES}>
              Priority
            </label>
            <div className="relative">
              <select
                id={priorityId}
                className={cn(INPUT_CLASSES, "appearance-none pr-10")}
                {...register("priority")}
              >
                <option value={FeedbackPriority.LOW}>Low</option>
                <option value={FeedbackPriority.MEDIUM}>Medium</option>
                <option value={FeedbackPriority.HIGH}>High</option>
                <option value={FeedbackPriority.CRITICAL}>Critical</option>
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-grey">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M2.5 4.5L6 8L9.5 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>
          </div>

          {/* Attachments */}
          <div className="flex flex-col gap-1.5">
            <span className={LABEL_CLASSES}>
              Attachments{" "}
              <span className="font-normal text-text-secondary">
                (optional, max {MAX_FILES})
              </span>
            </span>
            <AttachmentZone
              files={stagedFiles}
              onAdd={addFiles}
              onRemove={removeFile}
            />
            {fileError && (
              <p role="alert" className="text-xs text-error">
                {fileError}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-[#f0f0f0] px-6 py-5">
          <button
            type="button"
            onClick={onClose}
            disabled={isBusy}
            className="rounded-full border border-[#e8e8e9] px-6 py-3 text-body-md font-medium text-text-primary transition-colors hover:bg-bg-page disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isBusy}
            className="rounded-full bg-gradient-to-r from-gradient-start to-gradient-end px-6 py-3 text-body-md font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            {isBusy ? "Submitting..." : "Submit Feedback"}
          </button>
        </div>
      </form>
    </dialog>
  );
}
